import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCommercialDetail, uploadUserPhoto } from '../services/api';
import Chart from 'react-apexcharts';
import {
    FiArrowLeft, FiActivity, FiDollarSign, FiUsers, FiTarget,
    FiTrendingUp, FiTrendingDown, FiMapPin, FiMail, FiPhone, FiCalendar,
    FiStar, FiAlertCircle, FiCheckCircle, FiEye, FiShoppingBag,
    FiClock, FiAward, FiBriefcase, FiBarChart2, FiList, FiGrid, FiPieChart, FiCamera
} from 'react-icons/fi';

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isDev ? 'http://localhost:5056' : '';

const MOIS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const TYPE_COLORS: Record<string, string> = {
    visite: '#3B82F6', vente: '#16A34A', commande: '#8B5CF6',
    livraison: '#F59E0B', prospection: '#14B8A6', installation: '#EF4444',
    reclamation: '#EC4899', recouvrement: '#6366F1', formation: '#06B6D4'
};
const TYPE_ICONS: Record<string, string> = {
    visite: '👁', vente: '💰', commande: '📦',
    livraison: '🚚', prospection: '🔍', installation: '🔧',
    reclamation: '⚠️', recouvrement: '💳', formation: '📚'
};

const CommercialDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'resume' | 'activites' | 'clients' | 'objectifs'>('resume');

    const n = (v: any) => Math.round(Number(v) || 0);
    const fmt = (v: any) => n(v).toLocaleString('fr-FR');

    const loadDetail = () => {
        if (!id) return;
        setLoading(true);
        getCommercialDetail(parseInt(id))
            .then(res => setData(res.data))
            .catch(() => navigate('/admin/commerciaux'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadDetail(); }, [id]);

    const handlePhotoUpload = async (file: File) => {
        if (!id) return;
        try {
            await uploadUserPhoto(parseInt(id), file);
            loadDetail();
        } catch { /* ignore */ }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 500 }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, border: '3px solid #E2E8F0', borderTopColor: '#16A34A', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ color: '#94A3B8', fontSize: 14 }}>Chargement du profil...</p>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
    if (!data) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 500, color: '#EF4444', fontSize: 15 }}>
            Commercial non trouve
        </div>
    );

    const sm = data.stats_mois || {};
    const sp = data.stats_mois_precedent || {};
    const evolution = data.evolution || [];
    const activites = data.activites_recentes || [];
    const clients = data.clients_visites || [];
    const objectifs = data.objectifs || [];
    const parType = data.par_type || [];

    const growth = (curr: number, prev: number) => prev > 0 ? Math.round(((curr - prev) / prev) * 100) : (curr > 0 ? 100 : 0);
    const caGrowth = growth(n(sm.ca), n(sp.ca));
    const actGrowth = growth(n(sm.nb_activites), n(sp.nb_activites));
    const cliGrowth = growth(n(sm.nb_clients), n(sp.nb_clients));

    const scorePerf = Math.min(100, Math.round(
        (objectifs.length > 0
            ? objectifs.reduce((s: number, o: any) => s + Math.min(n(o.taux), 100), 0) / objectifs.length
            : (n(sm.nb_activites) > 0 ? 60 : 20))
    ));
    const perfColor = scorePerf >= 80 ? '#16A34A' : scorePerf >= 50 ? '#F59E0B' : '#EF4444';
    const perfLabel = scorePerf >= 80 ? 'Excellent' : scorePerf >= 50 ? 'Correct' : 'A ameliorer';

    return (
        <div>
            {/* =================== HERO HEADER =================== */}
            <div style={{
                background: 'linear-gradient(135deg, #14532D 0%, #166534 50%, #1A7A42 100%)',
                borderRadius: 16, padding: '28px 32px', marginBottom: 22, position: 'relative', overflow: 'hidden'
            }}>
                {/* Decorative circles */}
                <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
                <div style={{ position: 'absolute', bottom: -30, right: 100, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

                {/* Top row: back + status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <button onClick={() => navigate('/admin/commerciaux')}
                        style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
                        <FiArrowLeft size={18} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {data.jours_inactif <= 2 ? (
                            <span style={{ background: 'rgba(34,197,94,0.2)', color: '#86EFAC', padding: '5px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, backdropFilter: 'blur(4px)' }}>
                                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#4ADE80', marginRight: 6, verticalAlign: 'middle' }} />
                                Actif
                            </span>
                        ) : data.jours_inactif <= 7 ? (
                            <span style={{ background: 'rgba(251,191,36,0.2)', color: '#FDE68A', padding: '5px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                                <FiClock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {data.jours_inactif}j inactif
                            </span>
                        ) : (
                            <span style={{ background: 'rgba(239,68,68,0.2)', color: '#FCA5A5', padding: '5px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                                <FiAlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {data.jours_inactif}j inactif
                            </span>
                        )}
                        <span style={{ background: 'rgba(253,230,138,0.15)', color: '#FDE68A', padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                            <FiAward size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {fmt(data.points)} pts
                        </span>
                    </div>
                </div>

                {/* Profile row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        {data.photo_url ? (
                            <img src={`${API_URL}${data.photo_url}`} alt=""
                                style={{ width: 72, height: 72, borderRadius: 18, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
                        ) : (
                            <div style={{
                                width: 72, height: 72, borderRadius: 18,
                                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: 26, fontWeight: 800, letterSpacing: -0.5,
                                border: '2px solid rgba(255,255,255,0.2)'
                            }}>
                                {data.prenom?.[0]}{data.nom?.[0]}
                            </div>
                        )}
                        <label style={{
                            position: 'absolute', bottom: -3, right: -3, width: 24, height: 24, borderRadius: '50%',
                            background: '#fff', border: '2px solid rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                            <FiCamera size={11} color="#16A34A" />
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={e => { if (e.target.files?.[0]) handlePhotoUpload(e.target.files[0]); }} />
                        </label>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>
                            {data.prenom} {data.nom}
                        </h1>
                        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 8 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiMail size={13} /> {data.email}</span>
                            {data.telephone && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiPhone size={13} /> {data.telephone}</span>}
                            {data.zone_nom && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiMapPin size={13} /> {data.zone_nom}</span>}
                            {data.matricule && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FiBriefcase size={13} /> {data.matricule}</span>}
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {data.equipe_nom && (
                            <div style={{
                                background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 12, padding: '10px 18px', display: 'inline-block'
                            }}>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>Equipe</div>
                                <div style={{ color: '#FDE68A', fontWeight: 700, fontSize: 15 }}>
                                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: data.equipe_couleur || '#FDE68A', marginRight: 6, verticalAlign: 'middle' }} />
                                    {data.equipe_nom}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mini stats bar */}
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 22,
                    background: 'rgba(0,0,0,0.15)', borderRadius: 12, padding: '14px 20px'
                }}>
                    {[
                        { label: 'CA Mois', value: fmt(sm.ca) + ' F', icon: <FiDollarSign size={14} /> },
                        { label: 'Activites', value: String(n(sm.nb_activites)), icon: <FiActivity size={14} /> },
                        { label: 'Clients', value: String(n(sm.nb_clients)), icon: <FiUsers size={14} /> },
                        { label: 'Score', value: scorePerf + '%', icon: <FiTarget size={14} /> },
                    ].map((s, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{s.icon} {s.label}</div>
                            <div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>{s.value}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* =================== KPI CARDS =================== */}
            <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 22 }}>
                {[
                    { icon: <FiDollarSign size={22} />, color: '#16A34A', bg: '#DCFCE7', value: fmt(sm.ca) + ' F', label: 'Chiffre d\'affaires', growth: caGrowth },
                    { icon: <FiActivity size={22} />, color: '#3B82F6', bg: '#EFF6FF', value: String(n(sm.nb_activites)), label: 'Activites du mois', growth: actGrowth },
                    { icon: <FiUsers size={22} />, color: '#8B5CF6', bg: '#F3E8FF', value: String(n(sm.nb_clients)), label: 'Clients visites', growth: cliGrowth },
                    { icon: <FiShoppingBag size={22} />, color: '#F59E0B', bg: '#FEF3C7', value: String(n(sm.nb_ventes)), label: 'Ventes realisees', growth: null },
                    { icon: <FiEye size={22} />, color: '#14B8A6', bg: '#CCFBF1', value: String(n(sm.nb_visites)), label: 'Visites terrain', growth: null },
                ].map((kpi, i) => (
                    <div key={i} className="kpi-card" style={{
                        background: '#fff', borderRadius: 14, padding: '20px 18px', border: '1px solid #E2E8F0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden',
                        transition: 'box-shadow 0.2s, transform 0.2s', cursor: 'default'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: kpi.color, borderRadius: '14px 0 0 14px' }} />
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                            <div style={{ width: 46, height: 46, borderRadius: 12, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, flexShrink: 0 }}>
                                {kpi.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{kpi.label}</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#1E293B', letterSpacing: -0.3 }}>{kpi.value}</div>
                                {kpi.growth !== null && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                            color: kpi.growth >= 0 ? '#16A34A' : '#EF4444',
                                            background: kpi.growth >= 0 ? '#DCFCE7' : '#FEE2E2',
                                            display: 'inline-flex', alignItems: 'center', gap: 2
                                        }}>
                                            {kpi.growth >= 0 ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
                                            {kpi.growth >= 0 ? '+' : ''}{kpi.growth}%
                                        </span>
                                        <span style={{ fontSize: 11, color: '#94A3B8' }}>vs mois prec.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* =================== TABS =================== */}
            <div style={{
                display: 'flex', gap: 2, marginBottom: 22, background: '#fff', border: '1px solid #E2E8F0',
                padding: 4, borderRadius: 12, width: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
                {[
                    { key: 'resume', label: 'Resume', icon: <FiBarChart2 size={14} /> },
                    { key: 'activites', label: `Activites (${n(sm.nb_activites)})`, icon: <FiList size={14} /> },
                    { key: 'clients', label: `Clients (${clients.length})`, icon: <FiUsers size={14} /> },
                    { key: 'objectifs', label: `Objectifs (${objectifs.length})`, icon: <FiTarget size={14} /> }
                ].map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key as any)}
                        style={{
                            padding: '9px 20px', border: 'none', borderRadius: 9, cursor: 'pointer',
                            fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                            background: activeTab === t.key ? '#16A34A' : 'transparent',
                            color: activeTab === t.key ? '#fff' : '#64748B',
                            transition: 'all 0.2s', boxShadow: activeTab === t.key ? '0 2px 8px rgba(22,163,74,0.3)' : 'none'
                        }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* =================== TAB: RESUME =================== */}
            {activeTab === 'resume' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Row 1: Evolution + Performance Gauge + Repartition */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr', gap: 18 }}>
                        {/* Evolution Chart */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FiBarChart2 style={{ color: '#3B82F6' }} /> Evolution 6 mois
                                </h3>
                            </div>
                            {evolution.length > 0 ? (
                                <Chart type="area" height={260} options={{
                                    chart: { toolbar: { show: false }, background: 'transparent', fontFamily: 'DM Sans, sans-serif' },
                                    xaxis: {
                                        categories: evolution.map((e: any) => { const [y, m] = e.mois.split('-'); return MOIS[parseInt(m) - 1]; }),
                                        labels: { style: { colors: '#94A3B8', fontSize: '12px' } },
                                        axisBorder: { show: false }, axisTicks: { show: false }
                                    },
                                    yaxis: [
                                        { title: { text: '' }, labels: { style: { colors: '#94A3B8', fontSize: '11px' }, formatter: (v: number) => String(Math.round(v)) } },
                                        { opposite: true, title: { text: '' }, labels: { style: { colors: '#94A3B8', fontSize: '11px' }, formatter: (v: number) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : String(v) } }
                                    ],
                                    stroke: { curve: 'smooth', width: [2, 3] },
                                    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } },
                                    colors: ['#3B82F6', '#16A34A'],
                                    grid: { borderColor: '#F1F5F9', strokeDashArray: 4 },
                                    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px', fontWeight: 600, markers: { size: 6, shape: 'circle' as const } },
                                    tooltip: { y: { formatter: (v: number, { seriesIndex }: any) => seriesIndex === 1 ? fmt(v) + ' F' : String(v) } }
                                }} series={[
                                    { name: 'Activites', data: evolution.map((e: any) => n(e.activites)) },
                                    { name: 'CA (FCFA)', data: evolution.map((e: any) => n(e.ca)) }
                                ]} />
                            ) : <EmptyState text="Pas assez de donnees" />}
                        </div>

                        {/* Performance Score */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: '#1E293B', textAlign: 'center' }}>Score Performance</h3>
                            <div style={{ position: 'relative', width: 130, height: 130 }}>
                                <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                    <circle cx="50" cy="50" r="42" fill="none" stroke="#F1F5F9" strokeWidth="10" />
                                    <circle cx="50" cy="50" r="42" fill="none" stroke={perfColor} strokeWidth="10"
                                        strokeDasharray={`${scorePerf * 2.64} ${264 - scorePerf * 2.64}`}
                                        strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
                                </svg>
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ fontSize: 32, fontWeight: 800, color: perfColor, letterSpacing: -1 }}>{scorePerf}</span>
                                    <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>/ 100</span>
                                </div>
                            </div>
                            <span style={{
                                marginTop: 16, padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                color: perfColor, background: perfColor + '15'
                            }}>{perfLabel}</span>

                            {/* Quick comparison */}
                            <div style={{ width: '100%', marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { label: 'CA', pct: caGrowth },
                                    { label: 'Activites', pct: actGrowth },
                                    { label: 'Clients', pct: cliGrowth },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                        <span style={{ color: '#64748B', fontWeight: 500 }}>{item.label}</span>
                                        <span style={{
                                            fontWeight: 700, fontSize: 11, padding: '1px 8px', borderRadius: 10,
                                            color: item.pct >= 0 ? '#16A34A' : '#EF4444',
                                            background: item.pct >= 0 ? '#DCFCE7' : '#FEE2E2'
                                        }}>
                                            {item.pct >= 0 ? '+' : ''}{item.pct}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Repartition par type */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiPieChart style={{ color: '#8B5CF6' }} /> Par type
                            </h3>
                            {parType.length > 0 ? (
                                <>
                                    <Chart type="donut" height={200} options={{
                                        labels: parType.map((t: any) => t.type_activite),
                                        colors: parType.map((t: any) => TYPE_COLORS[t.type_activite] || '#94A3B8'),
                                        legend: { show: false },
                                        dataLabels: { enabled: false },
                                        plotOptions: { pie: { donut: { size: '65%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '13px', fontWeight: '700', color: '#1E293B' }, value: { fontSize: '20px', fontWeight: '800', color: '#1E293B' } } } } },
                                        stroke: { width: 2, colors: ['#fff'] }
                                    }} series={parType.map((t: any) => n(t.total))} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                        {parType.map((t: any, i: number) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLORS[t.type_activite] || '#94A3B8' }} />
                                                    <span style={{ color: '#475569', fontWeight: 500, textTransform: 'capitalize' }}>{t.type_activite}</span>
                                                </div>
                                                <span style={{ fontWeight: 700, color: '#1E293B' }}>{n(t.total)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : <EmptyState text="Aucune activite ce mois" />}
                        </div>
                    </div>

                    {/* Row 2: Infos + Comparaison + Top Clients */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
                        {/* Informations */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Informations</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { icon: <FiUsers size={14} />, label: 'Manager', value: data.manager_nom || 'Non assigne' },
                                    { icon: <FiMapPin size={14} />, label: 'Zone', value: data.zone_nom || 'Non assigne' },
                                    { icon: <FiGrid size={14} />, label: 'Equipe', value: data.equipe_nom || 'Non assigne', color: data.equipe_couleur },
                                    { icon: <FiBriefcase size={14} />, label: 'Matricule', value: data.matricule || '-' },
                                    { icon: <FiClock size={14} />, label: 'Derniere connexion', value: data.derniere_connexion ? new Date(data.derniere_connexion).toLocaleString('fr-FR') : 'Jamais' },
                                    { icon: <FiCalendar size={14} />, label: 'Membre depuis', value: new Date(data.created_at).toLocaleDateString('fr-FR') },
                                ].map((info, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                                        background: '#F8FAF9', borderRadius: 10, border: '1px solid #F1F5F9'
                                    }}>
                                        <div style={{ color: '#94A3B8', flexShrink: 0 }}>{info.icon}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>{info.label}</div>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: info.color || '#1E293B', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Comparaison mois precedent */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>vs Mois precedent</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[
                                    { label: 'Chiffre d\'affaires', curr: n(sm.ca), prev: n(sp.ca), format: (v: number) => fmt(v) + ' F', color: '#16A34A' },
                                    { label: 'Activites', curr: n(sm.nb_activites), prev: n(sp.nb_activites), format: (v: number) => String(v), color: '#3B82F6' },
                                    { label: 'Clients', curr: n(sm.nb_clients), prev: n(sp.nb_clients), format: (v: number) => String(v), color: '#8B5CF6' },
                                ].map((item, i) => {
                                    const pct = growth(item.curr, item.prev);
                                    const barWidth = item.prev > 0 ? Math.min(100, (item.curr / Math.max(item.curr, item.prev)) * 100) : (item.curr > 0 ? 100 : 0);
                                    return (
                                        <div key={i} style={{ padding: '14px', background: '#F8FAF9', borderRadius: 12, border: '1px solid #F1F5F9' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                <span style={{ fontWeight: 600, fontSize: 13, color: '#475569' }}>{item.label}</span>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                                                    color: pct >= 0 ? '#16A34A' : '#EF4444',
                                                    background: pct >= 0 ? '#DCFCE7' : '#FEE2E2'
                                                }}>
                                                    {pct >= 0 ? '+' : ''}{pct}%
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                                <span style={{ fontWeight: 800, fontSize: 18, color: '#1E293B' }}>{item.format(item.curr)}</span>
                                                <span style={{ color: '#94A3B8', alignSelf: 'flex-end' }}>vs {item.format(item.prev)}</span>
                                            </div>
                                            <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${barWidth}%`, background: `linear-gradient(90deg, ${item.color}, ${item.color}AA)`, borderRadius: 3, transition: 'width 0.8s ease' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Derniers clients visites */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Derniers clients</h3>
                            {clients.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {clients.slice(0, 6).map((c: any, i: number) => (
                                        <div key={i} style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                            borderRadius: 10, cursor: 'pointer', transition: 'background 0.15s',
                                            border: '1px solid transparent'
                                        }}
                                        onClick={() => navigate(`/admin/clients/${c.id}`)}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#F8FAF9'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
                                            <div style={{
                                                width: 34, height: 34, borderRadius: 8, background: '#EFF6FF',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#3B82F6', fontSize: 12, fontWeight: 700, flexShrink: 0
                                            }}>
                                                {(c.nom || '?')[0]}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nom}</div>
                                                <div style={{ fontSize: 11, color: '#94A3B8' }}>{c.ville || '-'} {c.segment ? `· ${c.segment}` : ''}</div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#3B82F6' }}>{n(c.nb_visites)}</div>
                                                <div style={{ fontSize: 10, color: '#94A3B8' }}>visites</div>
                                            </div>
                                        </div>
                                    ))}
                                    {clients.length > 6 && (
                                        <button onClick={() => setActiveTab('clients')}
                                            style={{ background: 'none', border: 'none', color: '#16A34A', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '8px 0', textAlign: 'center' }}>
                                            Voir tous les {clients.length} clients →
                                        </button>
                                    )}
                                </div>
                            ) : <EmptyState text="Aucun client visite" />}
                        </div>
                    </div>
                </div>
            )}

            {/* =================== TAB: ACTIVITES =================== */}
            {activeTab === 'activites' && (
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1E293B' }}>30 dernieres activites</h3>
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>{activites.length} resultats</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#F8FAF9' }}>
                                {['Date', 'Type', 'Client', 'Montant', 'Commentaire'].map((h, i) => (
                                    <th key={i} style={{
                                        padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#94A3B8',
                                        textTransform: 'uppercase', letterSpacing: 0.5, textAlign: i === 3 ? 'right' : 'left',
                                        borderBottom: '1px solid #E2E8F0'
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {activites.map((a: any) => (
                                <tr key={a.id} style={{ borderBottom: '1px solid #F1F5F9', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(22,163,74,0.02)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#1E293B' }}>
                                        <div style={{ fontWeight: 500 }}>{new Date(a.horodatage).toLocaleDateString('fr-FR')}</div>
                                        <div style={{ color: '#94A3B8', fontSize: 11 }}>{new Date(a.horodatage).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                            background: (TYPE_COLORS[a.type_activite] || '#94A3B8') + '14',
                                            color: TYPE_COLORS[a.type_activite] || '#64748B',
                                            textTransform: 'capitalize'
                                        }}>
                                            {TYPE_ICONS[a.type_activite] || '📋'} {a.type_activite}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>{a.client_nom || '-'}</div>
                                        {a.client_ville && <div style={{ fontSize: 11, color: '#94A3B8' }}>{a.client_ville}</div>}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: n(a.montant) > 0 ? '#16A34A' : '#CBD5E1' }}>
                                        {n(a.montant) > 0 ? fmt(a.montant) + ' F' : '-'}
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748B', maxWidth: 240 }}>
                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.commentaire || '-'}</div>
                                    </td>
                                </tr>
                            ))}
                            {activites.length === 0 && (
                                <tr><td colSpan={5}><EmptyState text="Aucune activite recente" /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* =================== TAB: CLIENTS =================== */}
            {activeTab === 'clients' && (
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Clients visites ce mois</h3>
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>{clients.length} clients</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#F8FAF9' }}>
                                {['Client', 'Ville', 'Segment', 'Visites', 'Derniere visite'].map((h, i) => (
                                    <th key={i} style={{
                                        padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#94A3B8',
                                        textTransform: 'uppercase', letterSpacing: 0.5, textAlign: i === 3 ? 'center' : 'left',
                                        borderBottom: '1px solid #E2E8F0'
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c: any) => (
                                <tr key={c.id}
                                    style={{ borderBottom: '1px solid #F1F5F9', cursor: 'pointer', transition: 'background 0.15s' }}
                                    onClick={() => navigate(`/admin/clients/${c.id}`)}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(22,163,74,0.02)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <td style={{ padding: '12px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3B82F6', fontSize: 12, fontWeight: 700 }}>
                                                {(c.nom || '?')[0]}
                                            </div>
                                            <span style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>{c.nom}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748B' }}>{c.ville || '-'}</td>
                                    <td style={{ padding: '12px 16px' }}>
                                        {c.segment ? (
                                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, background: '#F1F5F9', fontWeight: 600, color: '#475569' }}>{c.segment}</span>
                                        ) : <span style={{ color: '#CBD5E1', fontSize: 12 }}>-</span>}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                        <span style={{ fontWeight: 700, fontSize: 14, color: '#1E293B', background: '#EFF6FF', padding: '3px 12px', borderRadius: 20 }}>{n(c.nb_visites)}</span>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748B' }}>
                                        {c.derniere_visite ? new Date(c.derniere_visite).toLocaleDateString('fr-FR') : '-'}
                                    </td>
                                </tr>
                            ))}
                            {clients.length === 0 && (
                                <tr><td colSpan={5}><EmptyState text="Aucun client visite ce mois" /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* =================== TAB: OBJECTIFS =================== */}
            {activeTab === 'objectifs' && (
                <div>
                    {objectifs.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                            {objectifs.map((obj: any) => {
                                const taux = n(obj.taux);
                                const sc = taux >= 100 ? '#16A34A' : taux >= 70 ? '#F59E0B' : '#EF4444';
                                const scBg = taux >= 100 ? '#DCFCE7' : taux >= 70 ? '#FEF3C7' : '#FEE2E2';
                                const statusIcon = taux >= 100 ? <FiCheckCircle size={18} /> : taux >= 70 ? <FiTarget size={18} /> : <FiAlertCircle size={18} />;
                                const statusLabel = taux >= 100 ? 'Atteint' : taux >= 70 ? 'En cours' : 'En retard';
                                return (
                                    <div key={obj.id} style={{
                                        background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0',
                                        padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden',
                                        transition: 'box-shadow 0.2s, transform 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                        {/* Colored top strip */}
                                        <div style={{ height: 4, background: `linear-gradient(90deg, ${sc}, ${sc}88)` }} />
                                        <div style={{ padding: '20px 22px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 16, textTransform: 'capitalize', color: '#1E293B' }}>{obj.type_objectif}</div>
                                                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                                                        <FiCalendar size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                                        {obj.periode} · {new Date(obj.date_debut).toLocaleDateString('fr-FR')} - {new Date(obj.date_fin).toLocaleDateString('fr-FR')}
                                                    </div>
                                                </div>
                                                <span style={{ color: sc, background: scBg, borderRadius: 10, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                                                    {statusIcon} {statusLabel}
                                                </span>
                                            </div>

                                            {/* Values */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                                <div>
                                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Realise</div>
                                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#1E293B', letterSpacing: -0.5 }}>{fmt(obj.valeur_actuelle)}</div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Cible</div>
                                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#CBD5E1', letterSpacing: -0.5 }}>{fmt(obj.valeur_cible)}</div>
                                                </div>
                                            </div>

                                            {/* Progress bar */}
                                            <div style={{ height: 10, background: '#F1F5F9', borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
                                                <div style={{
                                                    height: '100%', width: `${Math.min(taux, 100)}%`,
                                                    background: `linear-gradient(90deg, ${sc}, ${sc}CC)`,
                                                    borderRadius: 5, transition: 'width 0.8s ease'
                                                }} />
                                            </div>
                                            <div style={{ textAlign: 'right', fontWeight: 800, fontSize: 16, color: sc }}>{taux}%</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '60px 40px', textAlign: 'center' }}>
                            <FiTarget size={40} style={{ color: '#CBD5E1', marginBottom: 12 }} />
                            <p style={{ color: '#94A3B8', fontSize: 15, margin: 0 }}>Aucun objectif en cours</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ---- Reusable empty state ---- */
const EmptyState: React.FC<{ text: string }> = ({ text }) => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <FiActivity size={28} style={{ color: '#CBD5E1', marginBottom: 8 }} />
        <p style={{ color: '#94A3B8', fontSize: 13, margin: 0 }}>{text}</p>
    </div>
);

export default CommercialDetail;
